import { Button } from "@/components/ui/button";
import {useState} from "react";
import {User, UserRole} from "../models/User";
import {useQueryClient} from "@tanstack/react-query";
import UsersRepository from "../repositories/UsersRepository";
import { useNotifiedMutation } from "../hooks/useNotifiedMutation";
import ConfirmationModal from "../components/common/ConfirmationModal";
import {CreateUserModal} from "../components/Users/CreateUserModal";
import {ResetPasswordModal} from "../components/Users/ResetPasswordModal";
import {UsersTable} from "../components/Users/UsersTable";
import {QUERY_KEYS} from "../constants/QueryKeys";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";

interface CreateUser {
  username: string;
  email: string;
  password: string;
  role: UserRole;
}

const UsersPage = () => {
  const [createdUser, setCreatedUser] = useState<User | null>(null);
  const [createUserModalOpened, setCreateUserModalOpened] = useState<boolean>(false);

  const [deleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
  const [deleteUserName, setDeleteUserName] = useState<string | null>(null);

  const [reactivateModalOpened, setReactivateModalOpened] = useState<boolean>(false);
  const [reactivateUserId, setReactivateUserId] = useState<string | null>(null);
  const [reactivateUserName, setReactivateUserName] = useState<string | null>(null);

  const [resetPasswordUser, setResetPasswordUser] = useState<{ id: string; username: string; email: string | null } | null>(null);

  const usersRepository = new UsersRepository();
  const queryClient = useQueryClient();

  const createUserMutation = useNotifiedMutation({
    mutationKey: ["user", createdUser?.id],
    mutationFn: async (newUser: CreateUser) => {
      return await usersRepository.createUser(
        newUser.username,
        newUser.email,
        newUser.password,
        newUser.role,
      );
    },
    successMessage: (_, vars) => `User "${vars.username}" created`,
    errorMessage: (err) => err?.message ?? "Failed to create user",
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ASSIGNABLE_USERS] });
    },
  });

  const deleteUserMutation = useNotifiedMutation({
    mutationFn: async (userId: string) => {
      await usersRepository.deleteUser(userId);
    },
    successMessage: () =>
      deleteUserName ? `User "${deleteUserName}" deleted` : "User deleted",
    errorMessage: (err) => err?.message ?? "Failed to delete user",
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ASSIGNABLE_USERS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATIONS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION_NAVIGATION_INFO] });
    },
  });

  const reactivateUserMutation = useNotifiedMutation({
    mutationFn: async (userId: string) => {
      await usersRepository.reactivateUser(userId);
    },
    successMessage: () =>
      reactivateUserName ? `User "${reactivateUserName}" reactivated` : "User reactivated",
    errorMessage: (err) => err?.message ?? "Failed to reactivate user",
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS] });
    },
  });

  const resetPasswordMutation = useNotifiedMutation({
    mutationFn: async ({ userId, newPassword }: { userId: string; newPassword: string }) => {
      await usersRepository.resetPassword(userId, newPassword);
    },
    successMessage: () => "Password reset successfully",
    errorMessage: (err) => err?.message ?? "Failed to reset password",
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS] });
    },
  });

  const handleConfirmDelete = async () => {
    if (deleteUserId) {
      try {
        await deleteUserMutation.mutateAsync(deleteUserId);
      } catch {
        // toast already shown by useNotifiedMutation
      }
    }
    setDeleteUserId(null);
    setDeleteUserName(null);
    setDeleteModalOpened(false);
  };

  const handleConfirmReactivate = async () => {
    if (reactivateUserId) {
      try {
        await reactivateUserMutation.mutateAsync(reactivateUserId);
      } catch {
        // toast already shown by useNotifiedMutation
      }
    }
    setReactivateUserId(null);
    setReactivateUserName(null);
    setReactivateModalOpened(false);
  };

  const handleCreateUser = async function (
    username: string,
    email: string,
    password: string,
    role: UserRole,
  ): Promise<void> {
    const user = await createUserMutation.mutateAsync({
      username: username,
      email: email,
      password: password,
      role: role,
    });
    setCreatedUser(user);
  };

  const handleResetPassword = async (newPassword: string): Promise<void> => {
    if (!resetPasswordUser) return;
    await resetPasswordMutation.mutateAsync({ userId: resetPasswordUser.id, newPassword });
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <CreateUserModal
        open={createUserModalOpened}
        onClose={() => setCreateUserModalOpened(false)}
        onCreateUser={handleCreateUser}
      />

      <ConfirmationModal
        open={deleteModalOpened}
        onClose={() => {
          setDeleteUserId(null);
          setDeleteUserName(null);
          setDeleteModalOpened(false);
        }}
        onConfirm={async () => {
          if (deleteUserId) await handleConfirmDelete();
        }}
        message="Are you sure you want to delete this user?"
        statusColor="danger"
        buttonText="Delete"
      >
        <div className="flex flex-wrap">
          <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold">
            {deleteUserName}
          </span>
        </div>
      </ConfirmationModal>

      <ConfirmationModal
        open={reactivateModalOpened}
        onClose={() => {
          setReactivateUserId(null);
          setReactivateUserName(null);
          setReactivateModalOpened(false);
        }}
        onConfirm={async () => {
          if (reactivateUserId) await handleConfirmReactivate();
        }}
        message="Are you sure you want to reactivate this user?"
        statusColor="success"
        buttonText="Reactivate"
      >
        <div className="flex flex-wrap">
          <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold">
            {reactivateUserName}
          </span>
        </div>
      </ConfirmationModal>

      <ResetPasswordModal
        open={!!resetPasswordUser}
        user={resetPasswordUser}
        onClose={() => setResetPasswordUser(null)}
        onReset={handleResetPassword}
      />

      <div className="flex my-2 flex-col sm:flex-row items-start sm:items-center flex-wrap justify-between shrink-0">
        <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">Users<DocsHelpLink to="administration/users" /></h2>
        <Button
          size="sm"
          onClick={() => setCreateUserModalOpened(true)}
        >
          Create User
        </Button>
      </div>

      <UsersTable
        onDeleteUser={(userId, username) => {
          setDeleteModalOpened(true);
          setDeleteUserId(userId);
          setDeleteUserName(username);
        }}
        onReactivateUser={(userId, username) => {
          setReactivateModalOpened(true);
          setReactivateUserId(userId);
          setReactivateUserName(username);
        }}
        onResetPassword={(userId, username, email) => {
          setResetPasswordUser({ id: userId, username, email });
        }}
      />
    </div>
  );
};

export default UsersPage;
